// NOTE: all modules imported below may be imported from '@silevis/reactgrid'
import * as React from "react";
import {
    CellTemplate,
    Cell,
    Compatible,
    Uncertain,
    UncertainCompatible,
    isNavigationKey,
    getCellProperty,
    isAlphaNumericKey,
    keyCodes
} from "@silevis/reactgrid";
import { useMeasure } from "@uidotdev/usehooks";
import Buttoon from "components/Buttoon";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { EditorRenderer, useTriggerEditCallback } from "./ExcelUtils";
import Highlighter from "react-highlight-words";

export interface StartChatCell extends Cell {
    type: "startchat";
    text: string;
    filter: string;
    placeholder?: string;
    validator?: (text: string) => boolean;
    errorMessage?: string;
    rowIdx: number;
    colIdx: number;
    onHeightChanged?: (rowIdx: number, colIdx: number, height: number) => void;
}

const triggerKey = { key: "\x06", keyCode: 6, bubbles: true };

export class StartChatCellTemplate implements CellTemplate<StartChatCell> {
    private wasEscKeyPressed = false;

    getCompatibleCell(uncertainCell: Uncertain<StartChatCell>): Compatible<StartChatCell> {
        const text = getCellProperty(uncertainCell, "text", "string");
        const filter = getCellProperty(uncertainCell, "filter", "string");
        let placeholder: string | undefined;
        try {
            placeholder = getCellProperty(uncertainCell, "placeholder", "string");
        } catch {
            placeholder = "";
        }
        const value = parseFloat(text); // TODO more advanced parsing for all text based cells
        const rowIdx = getCellProperty(uncertainCell, "rowIdx", "number");
        const colIdx = getCellProperty(uncertainCell, "colIdx", "number");
        return { ...uncertainCell, text, filter, value, placeholder, rowIdx, colIdx };
    }

    update(
        cell: Compatible<StartChatCell>,
        cellToMerge: UncertainCompatible<StartChatCell>
    ): Compatible<StartChatCell> {
        return this.getCompatibleCell({ ...cell, text: cellToMerge.text, placeholder: cellToMerge.placeholder });
    }

    handleKeyDown(
        cell: Compatible<StartChatCell>,
        keyCode: number,
        ctrl: boolean,
        shift: boolean,
        alt: boolean,
        key: string
    ): { cell: Compatible<StartChatCell>; enableEditMode: boolean } {
        if (!ctrl && !alt && isAlphaNumericKey(keyCode) && !(shift && keyCode === keyCodes.SPACE))
            return { cell, enableEditMode: false };
        return { cell, enableEditMode: keyCode === triggerKey.keyCode };
    }

    handleCompositionEnd(
        cell: Compatible<StartChatCell>,
        eventData: any
    ): { cell: Compatible<StartChatCell>; enableEditMode: boolean } {
        console.log("composition", eventData);
        return { cell: { ...cell, text: eventData }, enableEditMode: true };
    }

    getClassName(cell: Compatible<StartChatCell>, isInEditMode: boolean): string {
        const isValid = cell.validator ? cell.validator(cell.text) : true;
        const className = cell.className ? cell.className : "";
        return `${isValid ? "valid" : "rg-invalid"} ${
            cell.placeholder && cell.text === "" ? "placeholder" : ""
        } ${className}`;
    }

    render(
        cell: Compatible<StartChatCell>,
        isInEditMode: boolean,
        onCellChanged: (cell: Compatible<StartChatCell>, commit: boolean) => void
    ): React.ReactNode {
        if (!isInEditMode) {
            const isValid = cell.validator ? cell.validator(cell.text) : true;
            const cellText = cell.text || cell.placeholder || "";
            const textToDisplay = !isValid && cell.errorMessage ? cell.errorMessage : cellText;
            return (
                <TextRenderer
                    text={textToDisplay}
                    filter={cell.filter}
                    rowIdx={cell.rowIdx}
                    colIdx={cell.colIdx}
                    onHeightChanged={cell.onHeightChanged}
                />
            );
        }

        return (
            <EditorRenderer
                rowIdx={cell.rowIdx}
                colIdx={cell.colIdx}
                onHeightChanged={cell.onHeightChanged}
                cellType="startchat"
                defaultValue={cell.text}
                onChange={(e: any) =>
                    onCellChanged(this.getCompatibleCell({ ...cell, text: e.currentTarget.value }), false)
                }
                onBlur={(e: any) => {
                    onCellChanged(
                        this.getCompatibleCell({ ...cell, text: e.currentTarget.value }),
                        !this.wasEscKeyPressed
                    );
                    this.wasEscKeyPressed = false;
                }}
                onCopy={(e: any) => e.stopPropagation()}
                onCut={(e: any) => e.stopPropagation()}
                onPaste={(e: any) => e.stopPropagation()}
                onPointerDown={(e: any) => e.stopPropagation()}
                placeholder={cell.placeholder}
                onKeyDown={(e: any) => {
                    if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) e.stopPropagation();
                    if (e.keyCode === keyCodes.ESCAPE) this.wasEscKeyPressed = true;
                }}
            />
        );
    }
}

function TextRenderer({
    text,
    filter,
    rowIdx,
    colIdx,
    onHeightChanged
}: {
    text: string;
    filter: string;
    rowIdx: number;
    colIdx: number;
    onHeightChanged: (rowIdx: number, colIdx: number, height: number) => void;
}) {
    const [ref, { height }] = useMeasure();
    const [btnRef, { height: btnHeight }] = useMeasure();
    React.useEffect(() => {
        onHeightChanged?.(rowIdx, colIdx, height + btnHeight);
    }, [btnHeight, colIdx, height, onHeightChanged, rowIdx]);

    const navigate = useNavigate();

    const triggerEdit = useTriggerEditCallback(rowIdx, colIdx, "startchat");

    return (
        <div className="w-full h-full">
            <div className="flex flex-col h-full justify-evenly items-center content-evenly">
                <div className="flex-1 w-full" onDoubleClick={triggerEdit}>
                    <p
                        ref={ref}
                        className={classNames("whitespace-pre-wrap text-content select-auto p-1", {
                            italic: !text
                        })}
                    >
                        <Highlighter searchWords={[filter]} textToHighlight={text || "Empty draft..."} />
                    </p>
                </div>
                <div ref={btnRef} className="p-1 border-t-[1px] border-gray w-[98%] flex justify-center">
                    <Buttoon onClick={() => navigate("new", { state: { target_row: rowIdx, target_col: colIdx } })}>
                        Generate
                    </Buttoon>
                </div>
            </div>
        </div>
    );
}
